import * as d3 from 'd3';

import { getChartChrome, getGateForceGridChrome } from '../../../../../theme/chartChrome';
import { getCssToken } from '../../../../../theme/getCssToken';
import { LIGHT_HEX, TOKEN_CSS_VARS } from '../../../../../theme/tokens';
import {
  BREAKPOINT_NARROW,
  BREAKPOINT_COMPACT,
  DEFAULT_CHART_WIDTH,
  DEFAULT_CHART_HEIGHT,
  DEFAULT_ANGLE_DOMAIN,
  DEFAULT_FORCE_DOMAIN,
  DOMAIN_PAD_FACTOR,
  DOMAIN_PAD_MIN,
  MIN_Y_SPAN,
  GHOST_OPACITY,
} from '../utils/constants';
import type { DisplayLine, TracePoint } from '../utils/types';
import type { ChartDimensions, ChartElements, RenderConfig } from './types';
import { setupZoomBehavior, setupBrush } from './zoom';
import { setupTooltip } from './tooltip';

export type { RenderConfig } from './types';

export class GateForceChartRenderer {
  private dimensions: ChartDimensions | null = null;
  private zoomBehavior: d3.ZoomBehavior<SVGGElement, unknown> | null = null;
  private zoomTransform: d3.ZoomTransform = d3.zoomIdentity;
  private currentXScale: d3.ScaleLinear<number, number> = d3.scaleLinear();
  private currentYScale: d3.ScaleLinear<number, number> = d3.scaleLinear();
  private geometryCache = new Map<string, { d: string; length?: number }>();
  private pathLengths: number[] = [];
  private activePathNodes: SVGPathElement[] = [];
  private ghostPathNodes: SVGPathElement[] = [];
  private tipMarkerNodes: SVGCircleElement[] = [];
  private lastProgress = 0;
  private indicatorLabel: d3.Selection<SVGTextElement, unknown, null, undefined> | null = null;
  private tooltipLines: DisplayLine[] = [];
  private cleanupFunctions: Array<() => void> = [];
  private pendingZoomFrame: number | null = null;
  private interactionLayerSel: d3.Selection<SVGGElement, unknown, null, undefined> | null = null;

  private static getResponsiveFontSizes(width: number) {
    if (width < 400) return { axisTick: 9, axisLabel: 10 };
    if (width < 600) return { axisTick: 10, axisLabel: 11 };
    if (width < 800) return { axisTick: 11, axisLabel: 12 };
    return { axisTick: 12, axisLabel: 13 };
  }

  render(config: RenderConfig): void {
    const {
      svgElement,
      containerElement,
      tooltipElement,
      clipPathId,
      width: rawWidth,
      height: rawHeight,
      visibleStrokeLines,
      visibleProfileLines,
      legendRowers,
      viewMode: _viewMode,
      progress,
      focusedRowerKey,
    } = config;

    if (!svgElement) return;

    // Match Power Analysis: measure SVG element, fall back to container, then passed dims
    const svgRect = svgElement.getBoundingClientRect();
    let width = svgRect.width > 0 ? svgRect.width : rawWidth;
    let height = svgRect.height > 0 ? svgRect.height : rawHeight;

    if ((!width || !height || width === rawWidth || height === rawHeight) && containerElement) {
      const containerRect = containerElement.getBoundingClientRect();
      if (containerRect.width > 0 && containerRect.height > 0) {
        width = containerRect.width;
        height = containerRect.height;
      }
    }

    width = width || DEFAULT_CHART_WIDTH;
    height = height || DEFAULT_CHART_HEIGHT;
    if (!width || !height) return;

    this.cleanup();

    const svg = d3.select(svgElement);
    svg.selectAll('*').remove();

    const fonts = GateForceChartRenderer.getResponsiveFontSizes(width);
    const margin = GateForceChartRenderer.getMargins(width);
    const plotWidth = Math.max(width - margin.left - margin.right, 50);
    const plotHeight = Math.max(height - margin.top - margin.bottom, 50);
    const isNarrow = width < BREAKPOINT_NARROW;
    const xTickCount = Math.max(4, Math.min(10, Math.round(plotWidth / 90)));
    const yTickCount = Math.max(4, Math.min(8, Math.round(plotHeight / 72)));
    const chrome = getChartChrome();
    const { stroke: gridStroke, opacity: gridOpacity } = getGateForceGridChrome();

    this.dimensions = { width, height, plotWidth, plotHeight, margin };

    svg
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'none')
      .attr('role', 'img')
      .attr(
        'aria-label',
        `Gate force signature showing ${legendRowers.length} rower${legendRowers.length === 1 ? '' : 's'} across ${visibleStrokeLines.length} trace${visibleStrokeLines.length === 1 ? '' : 's'}.`
      )
      .style('font-family', 'Inter, sans-serif');

    const elements = GateForceChartRenderer.createElements(svg, clipPathId, plotWidth, plotHeight);

    elements.root.attr('transform', `translate(${margin.left},${margin.top})`);
    elements.xAxis.attr('transform', `translate(0, ${plotHeight})`);

    elements.xLabel
      .attr('x', width / 2)
      .attr('y', height - 18)
      .attr('text-anchor', 'middle')
      .attr('font-size', fonts.axisLabel)
      .attr('font-weight', 500)
      .attr('fill', chrome.axisLabel)
      .text(isNarrow ? 'Gate Angle (°)' : 'Gate Angle (degrees)');

    elements.yLabel
      .attr('transform', 'rotate(-90)')
      .attr('x', -height / 2)
      .attr('y', 18)
      .attr('text-anchor', 'middle')
      .attr('font-size', fonts.axisLabel)
      .attr('font-weight', 500)
      .attr('fill', chrome.axisLabel)
      .text(isNarrow ? 'Force (kgf)' : 'Gate Force (kgf)');

    elements.indicator.attr('x', plotWidth - 6).attr('y', 16);
    elements.crosshair.attr('y1', 0).attr('y2', plotHeight);
    this.indicatorLabel = elements.indicator;

    // --- Scales ---

    const renderLines = [...visibleStrokeLines, ...visibleProfileLines];
    const allPoints = renderLines.flatMap((line) => line.data);
    // Use global extents (all rowers) when provided so the axis domain stays stable
    // across rower switching and mode transitions. Fall back to visible-data extent.
    const angleExtent =
      config.globalAngleExtent ??
      (d3.extent(allPoints, (p) => p.x) as [number, number] | undefined);
    const forceExtent =
      config.globalForceExtent ??
      (d3.extent(allPoints, (p) => p.y) as [number, number] | undefined);

    const xDomain: [number, number] = angleExtent
      ? [angleExtent[0] - 2, angleExtent[1] + 2]
      : DEFAULT_ANGLE_DOMAIN;

    let yDomain: [number, number] = DEFAULT_FORCE_DOMAIN;
    if (forceExtent && Number.isFinite(forceExtent[0]) && Number.isFinite(forceExtent[1])) {
      const [rawMin, rawMax] = forceExtent;
      const span = Math.max(rawMax - rawMin, 1);
      const pad = Math.max(span * DOMAIN_PAD_FACTOR, DOMAIN_PAD_MIN);
      let lower = rawMin - pad;
      let upper = rawMax + pad;
      lower = Math.min(lower, -4);
      if (upper - lower < MIN_Y_SPAN) {
        const mid = (upper + lower) / 2;
        lower = mid - MIN_Y_SPAN / 2;
        upper = mid + MIN_Y_SPAN / 2;
      }
      yDomain = [lower, upper];
    }

    const baseX = d3.scaleLinear().domain(xDomain).range([0, plotWidth]);
    const baseY = d3.scaleLinear().domain(yDomain).range([plotHeight, 0]);
    this.currentXScale = baseX.copy();
    this.currentYScale = baseY.copy();

    const lineGenerator = d3
      .line<TracePoint>()
      .defined((p) => p != null && Number.isFinite(p.x) && Number.isFinite(p.y))
      .curve(d3.curveCatmullRom.alpha(0.35))
      .x((p) => baseX(p.x))
      .y((p) => baseY(p.y));

    // --- Geometry cache ---

    const cachePrefix = `${plotWidth}|${plotHeight}|${xDomain[0]}|${xDomain[1]}|${yDomain[0]}|${yDomain[1]}`;
    Array.from(this.geometryCache.keys()).forEach((key) => {
      if (!key.startsWith(cachePrefix)) this.geometryCache.delete(key);
    });

    const getLineGeometryKey = (line: DisplayLine) => {
      const first = line.data[0];
      const last = line.data[line.data.length - 1];
      return `${cachePrefix}|${line.id}|${line.data.length}|${first?.x ?? ''}|${first?.y ?? ''}|${last?.x ?? ''}|${last?.y ?? ''}`;
    };

    const getLineGeometry = (line: DisplayLine) => {
      const key = getLineGeometryKey(line);
      const cached = this.geometryCache.get(key);
      if (cached?.d) return { key, d: cached.d };
      const d = lineGenerator(line.data) ?? '';
      this.geometryCache.set(key, { d });
      return { key, d };
    };

    // --- Axes helper ---

    const updateAxes = (
      xScale: d3.ScaleLinear<number, number>,
      yScale: d3.ScaleLinear<number, number>
    ) => {
      elements.xAxis.call(
        d3
          .axisBottom(xScale)
          .ticks(xTickCount)
          .tickFormat((d) => `${Math.round(d as number)}°`) as d3.Axis<d3.NumberValue>
      );
      elements.yAxis.call(
        d3
          .axisLeft(yScale)
          .ticks(yTickCount)
          .tickFormat((d) => `${Math.round(d as number)}`) as d3.Axis<d3.NumberValue>
      );

      elements.xAxis
        .selectAll('text')
        .attr('fill', chrome.axisTick)
        .attr('font-size', fonts.axisTick);
      elements.xAxis.selectAll('line').attr('stroke', chrome.axisLine);
      elements.xAxis.select('.domain').attr('stroke', chrome.axisLine);
      elements.yAxis
        .selectAll('text')
        .attr('fill', chrome.axisTick)
        .attr('font-size', fonts.axisTick);
      elements.yAxis.selectAll('line').attr('stroke', chrome.axisLine);
      elements.yAxis.select('.domain').attr('stroke', chrome.axisLine);

      const gridXTicks = xScale.ticks(xTickCount);
      const gridYTicks = yScale.ticks(yTickCount);

      elements.gridX
        .selectAll<SVGLineElement, number>('line')
        .data(gridXTicks)
        .join(
          (enter) =>
            enter
              .append('line')
              .attr('y1', 0)
              .attr('y2', plotHeight)
              .attr('stroke', gridStroke)
              .attr('stroke-opacity', gridOpacity)
              .attr('stroke-width', 1),
          (update) => update,
          (exit) => exit.remove()
        )
        .attr('x1', (d) => xScale(d))
        .attr('x2', (d) => xScale(d))
        .attr('stroke', gridStroke)
        .attr('stroke-opacity', gridOpacity);

      elements.gridY
        .selectAll<SVGLineElement, number>('line')
        .data(gridYTicks)
        .join(
          (enter) =>
            enter
              .append('line')
              .attr('x1', 0)
              .attr('x2', plotWidth)
              .attr('stroke', gridStroke)
              .attr('stroke-opacity', gridOpacity)
              .attr('stroke-width', 1),
          (update) => update,
          (exit) => exit.remove()
        )
        .attr('y1', (d) => yScale(d))
        .attr('y2', (d) => yScale(d))
        .attr('stroke', gridStroke)
        .attr('stroke-opacity', gridOpacity);
    };

    // --- Zoom/Brush ---

    const applyTransform = (transform: d3.ZoomTransform) => {
      elements.ghostGroup.attr('transform', transform.toString());
      elements.activeGroup.attr('transform', transform.toString());
      elements.profileGroup.attr('transform', transform.toString());
      elements.tipMarkersGroup.attr('transform', transform.toString());

      if (this.pendingZoomFrame) cancelAnimationFrame(this.pendingZoomFrame);
      this.pendingZoomFrame = requestAnimationFrame(() => {
        const cx = transform.rescaleX(baseX);
        const cy = transform.rescaleY(baseY);
        this.currentXScale = cx;
        this.currentYScale = cy;
        updateAxes(cx, cy);
        this.pendingZoomFrame = null;
      });
    };

    this.zoomBehavior = setupZoomBehavior({
      plotWidth,
      plotHeight,
      elements,
      onZoom: (transform) => {
        this.zoomTransform = transform;
        applyTransform(transform);
      },
    });

    const brush = setupBrush({
      plotWidth,
      plotHeight,
      elements,
      getZoomBehavior: () => this.zoomBehavior!,
    });

    // --- Render lines ---

    if (!renderLines.length) {
      elements.indicator.text('');
      this.pathLengths = [];
      this.activePathNodes = [];
      this.ghostPathNodes = [];
      this.tipMarkerNodes = [];
      updateAxes(baseX, baseY);
      elements.interactionLayer.call(brush as any).call(this.zoomBehavior as any);
      return;
    }

    this.renderLineLayers(
      elements,
      visibleStrokeLines,
      visibleProfileLines,
      getLineGeometry,
      progress,
      focusedRowerKey ?? null
    );
    elements.legend.selectAll('*').remove();
    elements.legendBackground.attr('width', 0).attr('height', 0);

    // --- Tooltip + keyboard ---

    this.tooltipLines = visibleStrokeLines.length > 0 ? visibleStrokeLines : visibleProfileLines;

    const tooltipCleanup = setupTooltip({
      elements,
      tooltipElement,
      getDimensions: () => this.dimensions,
      getCurrentXScale: () => this.currentXScale,
      getTooltipLines: () => this.tooltipLines,
    });
    this.cleanupFunctions.push(tooltipCleanup);

    // Keyboard handlers (playback + scrub)
    elements.interactionLayer.call(brush as any).call(this.zoomBehavior as any);

    this.interactionLayerSel = elements.interactionLayer;

    // Apply preserved zoom
    const initialTransform = this.zoomTransform;
    applyTransform(initialTransform);
    if (!d3.zoomTransform(elements.interactionLayer.node()!).k) {
      elements.interactionLayer.call(this.zoomBehavior.transform as any, initialTransform);
    }

    updateAxes(this.currentXScale, this.currentYScale);

    if (visibleStrokeLines.length === 0) elements.indicator.text('');
  }

  /** Lightweight DOM update for stroke-reveal animation (hot path). */
  updateProgress(progress: number, isPlaying: boolean): void {
    if (!this.activePathNodes.length || !this.pathLengths.length) return;

    const clamped = Math.min(Math.max(progress, 0), 100);
    const total = this.pathLengths.length;

    const delta = Math.abs(clamped - this.lastProgress);
    const shouldUpdateAll = delta > 6;

    const updatePath = (i: number) => {
      const node = this.activePathNodes[i];
      if (!node) return;
      const len = this.pathLengths[i];
      const pathStart = (i / total) * 100;
      const pathEnd = ((i + 1) / total) * 100;
      let p: number;
      if (clamped <= pathStart) p = 0;
      else if (clamped >= pathEnd) p = 1;
      else p = (clamped - pathStart) / (pathEnd - pathStart);
      node.setAttribute('stroke-dashoffset', String(len * (1 - p)));
    };

    if (shouldUpdateAll) {
      for (let i = 0; i < total; i += 1) updatePath(i);
    } else {
      const curIdx = Math.min(Math.floor((clamped / 100) * total), total - 1);
      const prevIdx = Math.min(Math.floor((this.lastProgress / 100) * total), total - 1);
      const lo = Math.max(Math.min(prevIdx, curIdx) - 1, 0);
      const hi = Math.min(Math.max(prevIdx, curIdx) + 1, total - 1);
      for (let i = lo; i <= hi; i += 1) updatePath(i);
    }

    this.lastProgress = clamped;
    this.updateAnimationVisuals(clamped, isPlaying);

    if (this.indicatorLabel) {
      if (clamped >= 100) this.indicatorLabel.text(`${total} strokes`);
      else if (clamped <= 0) this.indicatorLabel.text('');
      else {
        const idx = Math.min(Math.floor((clamped / 100) * total), total - 1);
        this.indicatorLabel.text(`Stroke ${idx + 1} / ${total}`);
      }
    }
  }

  resetZoom(animated: boolean): void {
    if (!this.zoomBehavior || !this.interactionLayerSel) return;
    const layer = this.interactionLayerSel;
    if (layer.empty()) return;

    if (animated) {
      layer
        .transition()
        .duration(600)
        .call(this.zoomBehavior.transform as any, d3.zoomIdentity);
    } else {
      layer.call(this.zoomBehavior.transform as any, d3.zoomIdentity);
    }
  }

  cleanup(): void {
    this.cleanupFunctions.forEach((fn) => fn());
    this.cleanupFunctions = [];
    if (this.pendingZoomFrame) {
      cancelAnimationFrame(this.pendingZoomFrame);
      this.pendingZoomFrame = null;
    }
  }

  // --- Private helpers ---

  private updateAnimationVisuals(progress: number, isPlaying: boolean): void {
    const total = this.activePathNodes.length;
    if (!total) return;

    const showGhosts = progress > 0 && progress < 100;

    for (let i = 0; i < this.ghostPathNodes.length; i += 1) {
      const ghost = this.ghostPathNodes[i];
      if (ghost) {
        ghost.setAttribute('opacity', showGhosts ? String(GHOST_OPACITY) : '0');
      }
    }

    // Tip marker: only during active playback (not when paused/scrubbing)
    const activeIdx =
      isPlaying && progress > 0 && progress < 100
        ? Math.min(Math.floor((progress / 100) * total), total - 1)
        : -1;

    for (let i = 0; i < total; i += 1) {
      const marker = this.tipMarkerNodes[i];
      const pathNode = this.activePathNodes[i];
      if (!marker || !pathNode) continue;

      if (i === activeIdx) {
        const pathStart = (i / total) * 100;
        const pathEnd = ((i + 1) / total) * 100;
        let p: number;
        if (progress <= pathStart) p = 0;
        else if (progress >= pathEnd) p = 1;
        else p = (progress - pathStart) / (pathEnd - pathStart);

        const len = this.pathLengths[i];
        const pt = pathNode.getPointAtLength(len * Math.max(p, 0.01));
        marker.setAttribute('cx', String(pt.x));
        marker.setAttribute('cy', String(pt.y));
        marker.style.opacity = '1';
      } else {
        marker.style.opacity = '0';
      }
    }
  }

  private static getMargins(chartWidth: number) {
    return {
      top: chartWidth < BREAKPOINT_NARROW ? 20 : 28,
      right: chartWidth < BREAKPOINT_NARROW ? 20 : chartWidth < BREAKPOINT_COMPACT ? 24 : 32,
      bottom: chartWidth < BREAKPOINT_NARROW ? 44 : 56,
      left: chartWidth < BREAKPOINT_NARROW ? 72 : chartWidth < BREAKPOINT_COMPACT ? 64 : 52,
    };
  }

  private static createElements(
    svg: d3.Selection<SVGSVGElement, unknown, null, undefined>,
    clipPathId: string,
    plotWidth: number,
    plotHeight: number
  ): ChartElements {
    const defs = svg.append('defs');
    defs
      .append('clipPath')
      .attr('id', `${clipPathId}-bounds`)
      .append('rect')
      .attr('x', 0)
      .attr('y', 0)
      .attr('width', plotWidth)
      .attr('height', plotHeight);

    const root = svg.append('g').attr('class', 'chart-root');
    const gridX = root.append('g').attr('class', 'grid-x');
    const gridY = root.append('g').attr('class', 'grid-y');
    const xAxis = root.append('g').attr('class', 'x-axis');
    const yAxis = root.append('g').attr('class', 'y-axis');
    const xLabel = svg.append('text');
    const yLabel = svg.append('text');
    const indicator = root
      .append('text')
      .attr('class', 'reveal-label')
      .attr('text-anchor', 'end')
      .attr('font-size', 11)
      .attr('fill', '#2563eb')
      .text('');
    const plotArea = root
      .append('g')
      .attr('class', 'plot-area')
      .attr('clip-path', `url(#${clipPathId}-bounds)`);
    const ghostGroup = plotArea.append('g').attr('class', 'lines-ghost-zoom');
    const activeGroup = plotArea.append('g').attr('class', 'lines-active-zoom');
    const profileGroup = plotArea.append('g').attr('class', 'lines-profile-zoom');
    const tipMarkersGroup = plotArea.append('g').attr('class', 'tip-markers-zoom');
    const legend = root.append('g').attr('class', 'gate-force-legend');
    const legendBackground = legend.append('rect');
    const crosshair = root
      .append('line')
      .attr('class', 'crosshair')
      .attr('stroke', '#94a3b8')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3 3')
      .style('opacity', 0)
      .style('pointer-events', 'none');
    const interactionLayer = root.append('g').attr('class', 'interaction-layer');

    return {
      svg,
      root,
      gridX,
      gridY,
      xAxis,
      yAxis,
      xLabel,
      yLabel,
      indicator,
      plotArea,
      ghostGroup,
      activeGroup,
      profileGroup,
      tipMarkersGroup,
      legend,
      legendBackground,
      crosshair,
      interactionLayer,
    };
  }

  private renderLineLayers(
    elements: ChartElements,
    visibleStrokeLines: DisplayLine[],
    visibleProfileLines: DisplayLine[],
    getLineGeometry: (line: DisplayLine) => { key: string; d: string },
    progress: number,
    focusedRowerKey: string | null
  ): void {
    const lineColor = (l: DisplayLine) => {
      if (focusedRowerKey && l.rower !== focusedRowerKey) return 'rgba(100, 100, 100, 0.25)';
      return l.color;
    };
    const lineOpacity = (l: DisplayLine) => {
      if (focusedRowerKey && l.rower !== focusedRowerKey) return 0.25;
      return l.opacity;
    };

    const ghostStroke = getCssToken(TOKEN_CSS_VARS.gray[400], LIGHT_HEX.gray[400]);

    // Ghost layer — grayed-out preview visible only during animation
    const ghostMerged = elements.ghostGroup
      .selectAll<SVGPathElement, DisplayLine>('path')
      .data(visibleStrokeLines, (line) => line.id)
      .join(
        (enter) => enter.append('path').attr('fill', 'none').attr('stroke-linecap', 'round'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('stroke', ghostStroke)
      .attr('stroke-width', (l) => Math.max(1, l.strokeWidth - 0.5))
      .attr('opacity', 0)
      .attr('d', (l) => getLineGeometry(l).d || undefined);

    this.ghostPathNodes = ghostMerged.nodes();

    // Active layer
    const activeMerged = elements.activeGroup
      .selectAll<SVGPathElement, DisplayLine>('path')
      .data(visibleStrokeLines, (line) => line.id)
      .join(
        (enter) =>
          enter
            .append('path')
            .attr('fill', 'none')
            .attr('class', 'gf-line-active')
            .attr('stroke-linecap', 'round'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('stroke', (l) => lineColor(l))
      .attr('stroke-width', (l) => l.strokeWidth)
      .attr('opacity', (l) => lineOpacity(l))
      .attr('d', (l) => getLineGeometry(l).d || undefined);

    // Profile layer
    elements.profileGroup
      .selectAll<SVGPathElement, DisplayLine>('path')
      .data(visibleProfileLines, (line) => line.id)
      .join(
        (enter) =>
          enter
            .append('path')
            .attr('fill', 'none')
            .attr('class', 'gf-line-profile')
            .attr('stroke-linecap', 'round')
            .attr('stroke-linejoin', 'round'),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('stroke', (l) => lineColor(l))
      .attr('stroke-width', (l) => l.strokeWidth)
      .attr('opacity', (l) => lineOpacity(l))
      .attr('stroke-dasharray', null)
      .attr('d', (l) => getLineGeometry(l).d || undefined);

    // Compute path lengths + initial stroke-dashoffset
    this.pathLengths = [];
    this.activePathNodes = activeMerged.nodes();
    const totalPaths = Math.max(this.activePathNodes.length, 1);
    const initProgress = Math.min(Math.max(progress, 0), 100);

    activeMerged.each(function (this: SVGPathElement, _d, i) {
      const len = this.getTotalLength();

      const pathStart = (i / totalPaths) * 100;
      const pathEnd = ((i + 1) / totalPaths) * 100;
      let pathProgress: number;
      if (initProgress <= pathStart) pathProgress = 0;
      else if (initProgress >= pathEnd) pathProgress = 1;
      else pathProgress = (initProgress - pathStart) / (pathEnd - pathStart);

      d3.select(this)
        .attr('stroke-dasharray', len)
        .attr('stroke-dashoffset', len * (1 - pathProgress));
    });

    this.pathLengths = this.activePathNodes.map((node) => node.getTotalLength());
    this.lastProgress = initProgress;

    // Tip markers — small dots at the drawing tip of each active stroke
    const tipMarkers = elements.tipMarkersGroup
      .selectAll<SVGCircleElement, DisplayLine>('circle')
      .data(visibleStrokeLines, (line) => line.id)
      .join(
        (enter) =>
          enter.append('circle').attr('r', 3.5).attr('stroke', '#fff').attr('stroke-width', 1.5),
        (update) => update,
        (exit) => exit.remove()
      )
      .attr('fill', (l) => l.color);

    this.tipMarkerNodes = tipMarkers.nodes();

    // Position markers for the initial progress value (not playing on init)
    this.updateAnimationVisuals(initProgress, false);
  }
}
